import { SQRT_3 } from '../constants';
import type { CartesianCoordinates, HexTileCoordinates } from './model/types';

/**
 * Hex grid distance between two axial cells (cube length: (|dq|+|dr|+|dq+dr|)/2).
 */
function axialHexDistance(a: HexTileCoordinates, b: HexTileCoordinates): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/**
 * Axial (q, r) → world XZ for a **pointy-top** hex lattice (Y is up).
 * Matches `CylinderGeometry(..., 6)` in Three.js: first cap vertex lies on +X, i.e. pointy layout.
 * (Flat-top spacing differs by a 30° rotation; using it here misaligns tile centers vs meshes.)
 */
function axialToWorld(coordinates: HexTileCoordinates, radius: number): CartesianCoordinates {
  const { q, r } = coordinates;

  return {
    x: radius * (SQRT_3 * q + (SQRT_3 / 2) * r),
    y: 0,
    z: radius * (3 / 2) * r,
  };
}

/**
 * World XZ → the axial cell containing that point (inverse of axialToWorld,
 * with cube rounding to the nearest hex).
 */
function worldToAxial(x: number, z: number, radius: number): HexTileCoordinates {
  const fq = ((SQRT_3 / 3) * x - z / 3) / radius;
  const fr = ((2 / 3) * z) / radius;
  const fs = -fq - fr;
  let q = Math.round(fq);
  let r = Math.round(fr);
  const s = Math.round(fs);
  const dq = Math.abs(q - fq);
  const dr = Math.abs(r - fr);
  const ds = Math.abs(s - fs);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  // Normalise -0 so keys built from the result match getHexTileMapKey output.
  return { q: q + 0, r: r + 0 };
}

export { axialHexDistance, axialToWorld, worldToAxial };
